/**
 * In-memory flag definitions for OpenFeature's LocalProvider.
 * This file contains the flag definitions for the LocalProvider.
 * It is used to define the flags that are available in the LocalProvider.
 * The flags are defined as an object with the flag key as the key and the flag definition as the value.
 * The flag definition is an object with the following properties:
 * - type: The type of the flag. This can be one of 'boolean', 'string', 'number', or 'object'.
 * - defaultValue: The default value of the flag. This is the value that will be returned if the flag is not found in the LocalProvider.
 * - description: A description of the flag. This is used to provide information about the flag to the user.
 * - values: An array of possible values for the flag. This is used to provide a list of possible values for the flag to the user.
 */

interface EvenSplitDefinition<T> {
  weightedSplit?: false
  values: T[]
}

interface WeightedSplitDefinition<T> {
  weightedSplit: true
  values: {
    value: T
    weight: number
  }[]
}

export type FeatureDefinition<T> = {
  type: 'boolean' | 'string' | 'number'
  defaultValue: T
  description: string
} & (EvenSplitDefinition<T> | WeightedSplitDefinition<T>)

export type FeaturePrimitive = boolean | string | number

export interface FeaturesDefinition {
  [key: string]: FeatureDefinition<FeaturePrimitive>
}

export const flags: FeaturesDefinition = {
  segment: {
    type: 'string',
    defaultValue: 'control',
    description: 'The segment to which the user belongs',
    values: ['control', 'test'],
  },
  layoutSkeleton: {
    type: 'string',
    defaultValue: 'control',
    description: 'The layout skeleton to use',
    weightedSplit: true,
    values: [
      {
        value: 'control',
        weight: 80,
      },
      {
        value: 'test',
        weight: 20,
      },
    ],
  },
  adsConfig: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of ads config to use',
    values: ['control', 'test'],
  },
  siteSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of site settings to use',
    values: ['control', 'test'],
  },
  layoutSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', 'test'],
  },
  contentDisplaySettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of content display settings to use',
    values: ['control', 'test'],
  },
  showArticleImage: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the article image',
    values: [true, false],
  },
  showContentTopAdPlacement: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the content top ad placement',
    values: [true, false],
  },
  showContentMiddleAdPlacement: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the content middle ad placement',
    values: [true, false],
  },
  showContentAboveButtonAdPlacement: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the content above button ad placement',
    values: [true, false],
  },
  showContentBelowButtonAdPlacement: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the content below button ad placement',
    values: [true, false],
  },
  showContentBelowImageAdPlacement: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the content below image ad placement',
    values: [true, false],
  },
}
